/**
 * CreationLimitDialog — single warning dialog for every per-app creation cap.
 *
 * Replaces the two former inline dialogs (virtual-screen-limit + create-limit).
 * The copy + title are selected from `notice.kind`, so a new limited app reuses
 * this dialog automatically — no new component, no new branch.
 *
 * scio1 refs: appManagementComponent.js:86-112 (virtualScreen) + 124-131 (generic).
 */
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@scio3/ui';
import { SafeHtml } from '@/components/shared/SafeHtml';
import type { CreationLimitNotice } from '../../utils/app-creation-limit';

type Props = {
  /** Non-null opens the dialog; null keeps it closed. */
  notice: CreationLimitNotice | null;
  /** White-label accounts get admin-contact copy instead of support@optisigns. */
  isWhiteLabel: boolean;
  onClose: () => void;
};

export function CreationLimitDialog({ notice, isWhiteLabel, onClose }: Props) {
  const { t } = useTranslation();

  // Literal i18n keys per branch (no dynamic key construction — keeps the
  // string extractor + Lokalise sync working). See react-i18n rule.
  const title = !notice
    ? ''
    : notice.kind === 'virtualScreen'
      ? t('Virtual Screen')
      : notice.kind === 'emergencyFeed'
        ? t('Emergency Feed')
        : notice.kind === 'limitationCreateAssetEnterprise'
          ? t('Enterprise Feature')
          : t('Limit Reached');

  let body = '';
  if (notice?.kind === 'virtualScreen') {
    body = isWhiteLabel
      ? t(
          'Virtual Screens could potentially be misused and cause unnecessary load on our servers. To create more Virtual Screens, please contact Admin with more details on how you would be using Virtual Screens in your organization.',
        )
      : t(
          'Virtual Screens could potentially be misused and cause unnecessary load on our servers. To create more Virtual Screens, please contact <a href="mailto:support@optisigns.com?subject=Virtual Screen limit inquiry">support@optisigns.com</a> with more details on how you would be using Virtual Screens in your organization.',
        );
  } else if (notice?.kind === 'emergencyFeed') {
    // Account-wide cap; copy interpolates the resolved limit. White-label is
    // not differentiated here — the support link is generic.
    body = t('EmergencyFeedlimited', { emergencyFeedLimit: notice.limit });
  } else if (notice?.kind === 'limitationCreateAssetEnterprise') {
    const vars = {
      name: notice.appName,
      maxNumber: notice.limit,
      currentNumber: notice.count,
      helpLink: notice.helpLink ?? '',
    };
    body = isWhiteLabel
      ? t('limitationCreateAssetEnterpriseWhiteLabel', vars)
      : t('limitationCreateAssetEnterprise', vars);
  } else if (notice) {
    body = isWhiteLabel
      ? t('limitationCreateAssetWhiteLabel', { name: notice.appName })
      : t('limitationCreateAsset', { name: notice.appName });
  }

  return (
    <AlertDialog
      open={notice !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent data-testid="creation-limit-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <SafeHtml
              as="div"
              className="text-base text-foreground [&_a]:text-primary [&_a]:hover:underline"
              html={body}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction data-testid="close-btn" onClick={onClose}>
            {t('Close')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
